// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, tap } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { SuppliersService } from 'src/app/modules/suppliers/suppliers.service';
import { PrintQrDialogComponent } from 'src/app/shared/components/dialogs/print-qr-dialog/print-qr-dialog.component';
import { PropertyInterface } from 'src/app/shared/models/property/property.interface';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { DocumentArchivesService } from "../../document-archives.service";
import DocumentTypeStructure from '../../documentTypes';
import { DocumentAreasInterface } from '../../documentTypes.interface';
import { applyDocumentSearch } from "../../insert-dialog-doc-search.utils";

/**
 * Script start
 */

@Component({
  selector: "app-property-detail-document-insert-dialog",
  templateUrl: "./document-insert-dialog.component.html",
  styleUrls: ["./document-insert-dialog.component.scss"]
})
export class PropertyDetailDocumentInsertDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true
    }
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true
    }
  };
  // END - Edit item form
  existingNotes: any[] = [];
  qrcodeData = JSON.stringify({
    code: "005073005071S000125C"
  });
  selectedDocs = [];
  displayedStructures = [];
  displayedStructuresContract = [];
  selectedStructures = [];
  selectedStructureFree = "";
  condoStructure: any;
  public documentTypeStructure: DocumentTypeStructure = new DocumentTypeStructure();
  topics: DocumentAreasInterface;
  property: PropertyInterface;
  documentArchive: any;
  documentArchiveId: string;
  unitaImmobiliari = false;
  key: string;
  title: string;
  subscriptionType: string;
  docType: string;
  docUploaded = false;
  barCode = "";
  previousDocType: string;
  contratti = false;

  // Dati aggiuntivi elenco/nuovo elemento
  suppliersName: any[] = [];
  yearsOfStorage: any[] = [];
  conductorName: any[] = [];
  nameInterventionArchiving: any[] = [];

  searchValue = "";

  documentTemplateId = "";
  documentTemplate: any[] = [];

  loggedUserAdmin = false;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyDetailDocumentInsertDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private suppliersService: SuppliersService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private settingsService: SettingsService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => {
        this.loggedUser = res;

        // Get logged user's role data
        const currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        if (typeof currentRole !== 'undefined') {
          this.loggedUserAdmin =
            currentRole.role.type === "admin" ||
            currentRole.role.type === "operator";
        }
      })
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Only admins/operators can choose between digital/paper documents
    if (this.authService.loggedUserRequestedRoleType$.getValue() === "master"
      || this.authService.loggedUserRequestedRoleType$.getValue() === "collaborator") {
      this.docType = "digital";
    }

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
    this.key = this.data.key;
    this.condoStructure = this.data.condoStructure;
    this.documentArchive = this.data.documentArchive;
    if (this.documentArchive.suppliersName) {
      this.suppliersName = this.documentArchive.suppliersName;
    }
    if (this.documentArchive.yearsOfStorage) {
      this.yearsOfStorage = this.documentArchive.yearsOfStorage;
    }
    if (this.documentArchive.conductorName) {
      this.conductorName = this.documentArchive.conductorName;
    }
    if (this.documentArchive.nameInterventionArchiving) {
      this.nameInterventionArchiving =
        this.documentArchive.nameInterventionArchiving;
    }
    this.subscriptionType = this.data.subscriptionType;
    this.barCode = this.data.barCode;

    if (!this.key) {
      this.toastrService.error("No key injected");
      return;
    }
    if (!this.condoStructure && this.subscriptionType !== "FREE" && this.subscriptionType !== "FREEBASIC") {
      this.toastrService.error("No structure injected");
      return;
    }
    if (!this.documentArchive) {
      this.toastrService.error("No archive injected");
      return;
    }
    if (!this.barCode) {
      this.toastrService.error("No barCode injected");
      return;
    }

    this.subscriptions.push(
      this.settingsService.getDocument().subscribe(
        (res) => {
          if (res.status) {
            if (
              res.data &&
              Array.isArray(res.data) &&
              res.data.length > 0 &&
              res.data[0].area &&
              Array.isArray(res.data[0].area)
            ) {
              this.documentTemplateId = res.data[0]._id;
              this.documentTemplate = res.data[0].area;

              // this.setVisibleDocsByStructures();
              this.getData();
            }
          }
        },
        (err) => {
          //
        }
      )
    );

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form basing on docType
   *
   * @since 1.0.0
   */
  onChooseDocType(type: string): void {
    this.docType = type;
    this.initForm("", "");

    setTimeout(() => {
      if (
        this.docType === 'digital'
        && (this.data.action === 'new' || this.data.action === 'edit')
      ) {
        this.watchFileChanges();
      }
    }, 500);
  }

  /**
   * Watch for file-uploader changes, if file was uploaded
   * scroll down on dialog bottom
   *
   * @since 1.0.0
   */
  watchFileChanges(): void {
    if (this.form.controls["documentFile"]) {
      this.subscriptions.push(
        this.form.controls["documentFile"].valueChanges.subscribe(
          res => {
            // A file was uploaded, scroll down dialog
            if (Array.isArray(res) && res.length) {
              const dialogWindow = document.getElementsByTagName("mat-dialog-container")[0]; // assuming we can have only one dialog opened
              if (dialogWindow) {
                const contentToScroll = dialogWindow.getElementsByClassName("mat-dialog-content");
                if (contentToScroll[0]) {
                  const bottomHeight = contentToScroll[0].scrollHeight;
                  contentToScroll[0].scrollTo({
                    top: bottomHeight,
                    behavior: "smooth",
                  });
                }
              }
            }
          },
          err => {
            this.helperService.handleError(err);
          }
        ));
    }
  }

  /**
   * Handle data and form init
   *
   * @since 1.0.0
   */
  getData() {
    // this.topics = this.documentTypeStructure.getSubDocsArea(this.key);
    if (this.key === 'unitaImmobiliari') {
      this.unitaImmobiliari = true;
    }
    let subscriptionFree = false;
    if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
      subscriptionFree = true;
    }
    this.topics = this.documentTypeStructure.getSubDocsArea2(this.key, this.documentTemplate, subscriptionFree);

    if (this.topics && this.topics.documents && Array.isArray(this.topics.documents)) {
      // Only Privacy
      if (
        this.subscriptionType === 'PRIVACY' &&
        !this.loggedUserAdmin
      ) {
        this.topics.documents = this.topics.documents.filter(
          (el: DocumentAreasInterface) => el.privacy
        )
      }

      // Align the list with the search box, empty as the dialog opens: every doc starts visible
      applyDocumentSearch(this.topics.documents, this.searchValue, this.lang);

      this.displayedStructures = [];
      this.displayedStructuresContract = [];

      this.title = this.topics.title.replace("properties.documents.", "");
      if (this.subscriptionType !== "FREE" && this.subscriptionType !== "FREEBASIC") {
        if (this.title === "contratti") {
          this.contratti = true;
          let index = -1;
          let activeStructures = false;

          const copiedDocuments = JSON.parse(JSON.stringify(this.topics.documents));
          copiedDocuments.forEach((el: any) =>{
            const structure = el.structure;
            const origin = el.origin;
            const key = el.key;
            if (structure && structure !== "") {
              if (this.condoStructure[structure].length) {
                activeStructures = false;
                this.condoStructure[structure].forEach((item: any) => {
                  if (typeof item.active !== "undefined" && item.active) {
                    let name = item.nome;
                    switch (structure) {
                      case "dipendenti":
                        name = `${item.nome} ${item.cognome}`;
                        break;
                      case "contatoreGas":
                        name = `${item.nome} - ${item.codicePdr}`;
                        break;
                      case "ascensore":
                        name = `${item.nome} - ${item.numeroMatricola}`;
                        break;
                      case "montauto":
                        name = `${item.nome} - ${item.numeroMatricola}`;
                        break;
                      case "contatoriAcqua":
                        name = `${item.nome} - ${item.codiceUtente}`;
                        break;
                      // case "estintori":
                      //   name = "Estintori";
                      //   break;
                    }
                    activeStructures = true;
                    const index = this.displayedStructuresContract.findIndex((structureEl: any) => {
                      return structureEl.id.toString() === item._id.toString()
                    });
                    if (index === -1) {
                      this.displayedStructuresContract.push({
                        id: item._id,
                        name: name,
                        value: false,
                        type: structure,
                        origin
                      });
                    }
                  }
                });
                if (!activeStructures) {
                  index = this.topics.documents.findIndex((item: any) => {
                    return item.key === key;
                  });
                  this.topics.documents.splice(index, 1);
                }
              } else {
                index = this.topics.documents.findIndex((item: any) => {
                  return item.key === key;
                });
                this.topics.documents.splice(index, 1);
                if (structure === "fornituraMt") {
                  index = this.topics.documents.findIndex((item: any) => {
                    return item.key === "HA64";
                  });
                  this.topics.documents.splice(index, 1);
                }
              }
            }
          });
        } else if (this.unitaImmobiliari) {
          const structure = 'unitaAbitativaCommerciale';
          this.condoStructure[structure].forEach((item: any) => {
            if (typeof item.active !== "undefined" && item.active) {
              this.displayedStructures.push({
                id: item._id,
                name: item.nome,
                value: false,
                type: structure
              });
            }
          });
        } else {
          for (const docs of this.documentTemplate) {
            if (!docs.deleted) {
              const content = docs.content;
              if (content && Array.isArray(content)) {
                const index = content.findIndex((subDocs: any) => {
                  return (
                    subDocs.title.replace("properties.documents.", "") === this.title
                  );
                });
                if (index > -1) {
                  const structure = content[index].structure;
                  if (structure && structure !== "") {
                    this.condoStructure[structure].forEach((item: any) => {
                      if (typeof item.active !== "undefined" && item.active) {
                        let name = item.nome;
                        switch (structure) {
                          case "dipendenti":
                            name = `${item.nome} ${item.cognome}`;
                            break;
                          case "contatoreGas":
                            name = `${item.nome} - ${item.codicePdr}`;
                            break;
                          case "ascensore":
                            name = `${item.nome} - ${item.numeroMatricola}`;
                            break;
                          case "montauto":
                            name = `${item.nome} - ${item.numeroMatricola}`;
                            break;
                          case "contatoriAcqua":
                            name = `${item.nome} - ${item.codiceUtente}`;
                            break;
                          // case "estintori":
                          //   name = "Estintori";
                          //   break;
                        }
                        this.displayedStructures.push({
                          id: item._id,
                          name: name,
                          value: false,
                          type: structure
                        });
                      }
                    });

                  } else {
                    if (this.title === "vvFdefinitivo") {
                      if (this.documentArchive.vvFdefinitivo
                        && Array.isArray(this.documentArchive.vvFdefinitivo)) {
                        this.documentArchive.vvFdefinitivo.forEach((item: any) => {
                          
                            if (item.structureObjectType !== "") {

                              this.displayedStructures.push({
                                id: item.structureObjectId,
                                name: item.structureObjectName,
                                value: false,
                                type: "vvFdefinitivo",
                                typeStructure: item.structureObjectType
                              });

                            } else if (item.structureObjectId === "-1") {
                              const structure = {
                                id: item.structureObjectId,
                                name: this.lang === "it" ? `Edificio uso terziario ${item.structureObjectName}` : `Tertiary use building ${item.structureObjectName}`,
                                value: false,
                                type: "vvFdefinitivo",
                                typeStructure: item.structureObjectType,
                              };
                              this.displayedStructures.push(structure);
                            }
                          
                        })
                      }
                    }
                  }
                  break;
                }
              }
            }
          }
        }
      } else {
        if (this.title === "contratti") {
          this.contratti = true;
          this.selectedStructureFree = "-1";
        } else {
          for (const docs of this.documentTemplate) {
            if (!docs.deleted) {
              const content = docs.content;
              if (content && Array.isArray(content)) {
                const index = content.findIndex((subDocs: any) => {
                  return (
                    subDocs.title.replace("properties.documents.", "") ===
                    this.title && subDocs.structure && subDocs.structure !== ""
                  );
                });
                if (index > -1) {
                  this.selectedStructureFree = "-1";
                }
              }
            }
          }
        }
      }
    }
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(docKey: string, schemaType: string): void {
    this.fields = [];
    this.model = {};
    if (this.form && this.form.controls) {
      for (const key in this.form.controls) {
        delete this.form.controls[key];
      }
    }
    // create default field to formly
    const defaultFields = [
      {
        className: "col-12",
        type: "textarea",
        key: "contenutoDocumento",
        templateOptions: {
          label: this.translateService.instant("properties.detail.documentArchive.fields.contenutoDocumento"),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.contenutoDocumento"
          ),
          required: true,
          minLength: 2,
          maxLength: 500,
          rows: 5,
        }
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "input",
        key: "protocollo",
        templateOptions: {
          readonly:false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.protocollo"
          ),
          required: false,
          minLength: 0,
          maxLength: 500,
        },
      },
      {
        className: "col-12 col-md-12 col-xl-12",
        type: "textarea",
        key: "keyword",
        templateOptions: {
          readonly:false,
          label: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          placeholder: this.translateService.instant(
            "properties.detail.documentArchive.fields.keyword"
          ),
          required: false,
          minLength: 0,
          maxLength: 1000,
          rows: 3
        },
      }
    ];

    // create fields formly from document key: es AA1
    // const aryfields = this.documentTypeStructure.getFormlyFields(docKey, false);
    const aryfields = this.documentTypeStructure.getFormlyFields2(
      schemaType,
      false
    );
    if (aryfields && aryfields.length) {
      aryfields.forEach((item: any) => {
        if (item.templateOptions && item.templateOptions.placeholder) {
          item.templateOptions.placeholder = this.translateService.instant(item.templateOptions.placeholder);
        }
        if (item.type === "select") {
          if (Array.isArray(item.templateOptions.options)) {
            item.templateOptions.options.forEach((subItem: any) => {
              subItem.label = this.translateService.instant(subItem.label);
            })
          }
        }
        if (item.type === "selectNew") {
          item.type = "autocomplete";
          if (item.templateOptions.initialValues) {
            const options: any[] = [];
            switch (item.templateOptions.initialValues) {
              case "suppliersName":
                this.suppliersName.forEach((subItem: any) => {
                  // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
                });
                break;
              case "yearsOfStorage":
                this.yearsOfStorage.forEach((subItem: any) => {
                  // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
                });
                break;
              case "conductorName":
                this.conductorName.forEach((subItem: any) => {
                  // options.push({
                //   value: subItem,
                //   label: subItem,
                // });
                options.push(subItem);
                });
                break;
              case "nameInterventionArchiving":
                this.nameInterventionArchiving.forEach((subItem: any) => {
                  // options.push({
                  //   value: subItem,
                  //   label: subItem,
                  // });
                  options.push(subItem);
                });
                break;
            }
            item.templateOptions.initialValues = options;
            item.templateOptions.addTagText = this.translateService.instant(
              item.templateOptions.addTagText
            );
            item.templateOptions.customLabel = this.translateService.instant(
              item.templateOptions.customLabel
            );
          }
        }
        if (
          item.type === "autocomplete" ||
          item.type === "autocomplete-supplier"
        ) {
          item.templateOptions.addTagText = this.translateService.instant(item.templateOptions.addTagText);
          item.templateOptions.customLabel = this.translateService.instant(item.templateOptions.customLabel);
          if (item.templateOptions.search$ === "suppliersService") {
            item.templateOptions.search$ = this.suppliersService.getListV2.bind(this)
          }
        } else {
          if (item.templateOptions && item.templateOptions.label) {
            item.templateOptions.label = this.translateService.instant(item.templateOptions.label);
          }
        }
      });

      const fieldGroup = aryfields.concat(defaultFields);
      const itemConfig = [
        {
          fieldGroupClassName: "row",
          fieldGroup: fieldGroup
        }
      ];

      this.fields = itemConfig;
      if (this.docType && this.docType === 'digital') {
        this.fields.push(
          {
            fieldGroupClassName: "row",
            fieldGroup: [
              {
                className: "col-12",
                type: "file-uploader",
                key: "documentFile",
                templateOptions: {
                  required: true,
                  allowedFilesNumber: 200,
                  allowedFilesExtensions: this.configService.allowedDocumentExtensions,
                  customLabel: this.translateService.instant('properties.detail.documentArchive.fields.file'),
                },
                // Show file upload only if file is not uploaded yet
                hideExpression:
                  typeof this.data.itemData !== 'undefined'
                  && this.data.itemData.document
                  && this.data.itemData.document._id !== ''
              }
            ]
          }
        );
      } else if (this.docType && this.docType === 'paper') {
        // For better fields displaying
        // this.dialogRef.updateSize('60vw');
        this.fields.push(
          {
            fieldGroupClassName: "row",
            fieldGroup: [
              {
                className: "col-6",
                type: "input",
                key: "numFiles",
                hooks: {
                  onInit: (field: any): void => {
                    field.formControl.valueChanges
                      .pipe(
                        tap(value => {
                          if (typeof value === "undefined") {
                            value = null;
                          }
                          if (value > field.templateOptions.max) {
                            value = field.templateOptions.max;
                          }
                          if (this.model.files) {
                            this.model.files.length = value;
                          }
                          this.model = {
                            ...this.model,
                            numFiles: value
                          };
                        })
                      )
                      .subscribe();
                  }
                },
                templateOptions: {
                  type: "number",
                  label: this.translateService.instant('properties.detail.documentArchive.fields.numFiles'),
                  required: false,
                  min: 2,
                  max: 100,
                },
                expressionProperties: {
                  // apply expressionProperty for disabled based on formState
                  "templateOptions.disabled": "formState.disabled"
                },
              },
              {
                className: "docs-radio-group-wrapper col-12",
                key: "files",
                type: "repeat",
                expressionProperties: {
                  // apply expressionProperty for disabled based on formState
                  "templateOptions.disabled": "formState.disabled"
                },
                fieldArray: {
                  fieldGroup: [
                    {
                      className: "pl-0 col-12 col-md-6",
                      type: "input",
                      key: "name",
                      hooks: {
                        onInit(field) {
                          const type = field.parent.fieldGroup[1].defaultValue[0]; // take first letter from radio's defaultValue
                          const name = this.barCode; // barcode value
                          const index = +field.parent.key + 1; // actual array index plus 1
                          field.defaultValue = `${name}_${type}${index}`.toUpperCase();
                        },
                      },
                      templateOptions: {
                        label: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                        placeholder: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                        minLength: 0,
                        maxLength: 500,
                        readonly: true,
                      },
                    },
                    {
                      className: "col-12 col-md-6",
                      type: "radio",
                      key: "docType",
                      defaultValue: "report",
                      expressionProperties: {
                        "templateOptions.onChange": (model: any, opt: any, field: any) => {
                          if (model) {
                            const position = +field.parent.key;

                            // @todo: da rivedere (segnalato in #105)
                            // Return if no changes were made
                            // if (
                            //   this.previousDocTypes.map(el => el.value).includes(field.formControl.value)
                            //   && this.previousDocTypes.map(el => el.position).includes(position)
                            // ) {
                            //   return;
                            // }
                            // this.previousDocTypes = [];
                            // field.parent.parent.fieldGroup.forEach(el => {
                            //   this.previousDocTypes.push({
                            //     position,
                            //     value: el.formControl.value.docType
                            //   });
                            // });

                            const type = model.docType[0]; // take first letter from radio's actual value
                            const name = this.barCode; // barcode value
                            const index = position + 1; // actual array index plus 1;

                            // Set new value in model
                            model.name = `${name}_${type}${index}`.toUpperCase();

                            // Set new value in field (make it change inside form input)
                            field.parent.fieldGroup[0].formControl.setValue(model.name);
                          }
                        },
                      },
                      templateOptions: {
                        label: this.translateService.instant('properties.detail.documentArchive.fields.docType'),
                        options: [
                          {
                            value: "report",
                            label: this.translateService.instant('properties.detail.documentArchive.fields.report'),
                          },
                          {
                            value: "tables",
                            label: this.translateService.instant('properties.detail.documentArchive.fields.tables'),
                          },
                          {
                            value: "calcs",
                            label: this.translateService.instant('properties.detail.documentArchive.fields.calcs'),
                          },
                          {
                            value: "schemas",
                            label: this.translateService.instant('properties.detail.documentArchive.fields.schemas'),
                          },
                          {
                            value: "various",
                            label: this.translateService.instant('properties.detail.documentArchive.fields.various'),
                          }
                        ],
                      }
                    },
                  ]
                }
              }
            ]
          }
        );
      } else {
        this.toastrService.error('Error: no docType selected');
      }

      setTimeout(() => {
        this.watchFileChanges();
      }, 500);
    }
  }


  /**
   * View the structures of select contracts
   *
   * @since 1.0.0
   */
  viewContractStructures(): void {
    let contractStructures = [];
    this.displayedStructures = [];
    this.topics.documents.forEach((el: any) => {
      if (el.value) {
        if (el.origin !== "" && el.structure && el.structure !== "") {
          contractStructures = this.displayedStructuresContract.filter((item: any) => {
            const index = this.displayedStructures.findIndex((subEl: any) => {
              return subEl.id === item.id
            });
            if (index === -1) {
              return item.type === el.structure;
            }
          });
          this.displayedStructures = this.displayedStructures.concat(contractStructures);
        }
      }
    });
  }

  /**
   * Handle enabling same docs type
   *
   * @since 1.0.0
   */
  onSetDoc(key: string, schemaType: string, checked: boolean, title: any, parentTitle?: string): void {
    let titleDoc = '';
    if (this.lang === "it") {
      titleDoc = title.it;
    } else {
      titleDoc = title.en;
    }
    const type = this.documentTypeStructure.getDocumentType2(key, this.documentTemplate);
    
    if (this.topics.title.replace("properties.documents.", "") === "contratti") {
      this.viewContractStructures();
    }

    // see if at least one checkbox is selected...
    let selected = false;
    if (this.topics.documents && Array.isArray(this.topics.documents)) {
      this.topics.documents.forEach((el: any) => {
        if (!checked) {
          if (el.key !== key && el.value) {
            selected = true;
          }
        } else {
          selected = true;
          if (this.documentTypeStructure.getDocumentType2(el.key, this.documentTemplate) === type) {
            el.disabled = false;
          } else {
            el.disabled = true;
          }
        }
      });
    }

    // ...otherwise, enable all checkbox...
    if (!selected) {
      this.topics.documents.forEach((el: any) => {
        el.disabled = false;
      });
      // ...and delete/reset form fields
      this.fields = [];
    } else {
      // If we're here means we have at least one checkbox selected
      if (
        type !== this.previousDocType
        || !this.selectedDocs.length
      ) {
        this.previousDocType = type;
        this.initForm(key, schemaType);
      }
    }

    // Handle selectedDocs array
    if (checked) {
      let subDoc = this.title;
      const title = this.topics.title.replace("properties.documents.", "");
      if (title === "contratti") {
        let contractStructures = [];
        const contracts = this.topics.documents.filter((el: any) => {
          return el.key === key;
        });
        if (contracts && Array.isArray(contracts) && contracts[0].origin) {
          // Questo qui: topic = R o R3 - RM5 - ND3 - LA8 - AD2
          subDoc = contracts[0].origin;

          if (subDoc === "impelegenerale-contratti") {
              contractStructures = this.selectedStructures.filter((item: any) => {
                return item.type === "fornituraBt";
              });
          } else if (subDoc === "impeleMediaTensione-contratti"){
              contractStructures = this.selectedStructures.filter((item: any) => {
                return item.type === "fornituraMt";
              });
          } else {  
            for (const docs of this.documentTemplate) {
              if (!docs.deleted) {
                const content = docs.content;
                if (content && Array.isArray(content)) {
                  const index = content.findIndex((subDocs: any) => {
                    return (
                      subDocs.title.replace("properties.documents.", "") ===
                      subDoc
                    );
                  });
                  if (index > -1) {
                    const structure = content[index].structure;
                    contractStructures = this.selectedStructures.filter((item: any) => {
                      return item.type === structure;
                    });
                  }
                }
              }
            }
          }
          const doc = {
            subDoc,
            subAreaTitle: "",
            title: titleDoc, // this.translateService.instant(`properties.documents.${title}`),
            key,
            structureSelected: contractStructures,
          }


          this.selectedDocs.push(doc);
        } else {
          const doc = {
            subDoc: "contratti",
            subAreaTitle: "",
            title: titleDoc, // this.translateService.instant(`properties.documents.${title}`),
            key,
            structureSelected: [],
          }
          this.selectedDocs.push(doc);
        }
      } else if (this.unitaImmobiliari) {
        const doc = {
          subDoc: parentTitle,
          subAreaTitle: "",
          title: titleDoc, // this.translateService.instant(`properties.documents.${title}`),
          key,
          structureSelected: this.selectedStructures,
        }
        this.selectedDocs.push(doc);
      } else {
        const doc = {
          subDoc,
          subAreaTitle: "",
          title: titleDoc, // this.translateService.instant(`properties.documents.${title}`),
          key,
          structureSelected: this.selectedStructures,
        }
        this.selectedDocs.push(doc);
      }
    } else {
      const index = this.selectedDocs.map((el) => el.key).indexOf(key);
      this.selectedDocs.splice(index, 1);
    }
  }

  /**
   * Check which structures are selected and then enable submit button
   *
   * @since 1.0.0
   */
  onChangePlant(item: any, checked?: boolean): void {
    // Handle selectedStructures array
    if (checked) {
      this.selectedStructures.push(item);
    } else {
      const index = this.selectedStructures.map((el) => el.id).indexOf(item.id);
      this.selectedStructures.splice(index, 1);
    }
    const title = this.topics.title.replace("properties.documents.", "");
    if (title === "contratti") {
      let contractStructures = [];
      if (Array.isArray(this.selectedDocs)) {
        this.selectedDocs.forEach((el: any) => {
          //   origin: "impelegenerale",
          if (el.subDoc !== "") {
            contractStructures = this.selectedStructures.filter((item: any) => {
              return item.origin === el.subDoc;
            });
          }
          el.structureSelected = contractStructures;
        });
      }
    }
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    if (this.data) {
      if (this.data.item) {
        this.data.item.note = this.existingNotes;
      }
    }
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {

    // Prevent invalid data submit
    if (this.contratti && this.subscriptionType !== "FREE" && this.subscriptionType !== "FREEBASIC") {
      if (!this.selectedDocs.length) {
        this.toastrService.error(
          this.translateService.instant("common.errors.chooseElementFirst")
        );
        return;
      }
      if (this.displayedStructures.length && !this.selectedStructures.length) {
        this.toastrService.error(
          this.translateService.instant("common.errors.chooseStructureFirst")
        );
        return;
      }

      let errStructure = false;
      this.selectedDocs.forEach((item: any) => {

        let found = false;
        if (this.documentTemplate && Array.isArray(this.documentTemplate)) {
          for (const docs of this.documentTemplate) {
            const content = docs.content;
            if (content && Array.isArray(content)) {
              for (const subDocs of content) {
                const title = subDocs.title.replace("properties.documents.", "");
                if (title === item.subDoc && subDocs.structure && subDocs.structure !== "") {
                  if (!item.structureSelected || !item.structureSelected.length) {

                    this.toastrService.error(
                      this.translateService.instant("common.errors.chooseElementFirst")
                    );
                    errStructure = true;
                  }
                  found = true;
                }
                if (found) {
                  break;
                }
              }
            }
            if (found) {
              break;
            }
          }
        }
        if (errStructure) {
          return;
        }
      });
      if (errStructure) {
        return;
      }
    } else {
      if (
        !this.selectedDocs.length
      ) {
        this.toastrService.error(
          this.translateService.instant("common.errors.chooseElementFirst")
        );
        return;
      }
      if (this.displayedStructures.length && !this.selectedStructures.length) {
        this.toastrService.error(
          this.translateService.instant("common.errors.chooseStructureFirst")
        );
        return;
      }
    }
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    if (this.contratti) {
      this.selectedDocs.forEach((item: any) => {
        switch (item.subDoc) {
          case "impelegenerale-contratti":
          case "impeleMediaTensione-contratti":
            item.subDoc = "contratti";
            break;
        }
      });
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    let dataToSend = {};
    if (this.form.value) {
      if (this.docType === 'digital') {
        dataToSend = {
          data: this.form.value,
          docsSelected: this.selectedDocs,
          documentFile: this.form.value.documentFile,
          contratti: this.contratti,
        }
        if (this.selectedStructures.length) {
          dataToSend = {
            ...dataToSend,
            structureSelected: this.selectedStructures,
          };
        }
        if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
          dataToSend = {
            ...dataToSend,
            structureSelected: this.selectedStructureFree,
          };
        }
        this.subscriptions.push(
          this.documentArchivesService
            .updateMultiDoc(
              this.documentArchive._id,
              dataToSend
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              res => {
                if (res.status) {
                  if (res.data && typeof res.data === "string") {
                    this.barCode = res.data.toUpperCase();
                    this.docUploaded = true;
                  }
                  this.toastrService.success(res.message);
                  // this.dialogRef.close({
                  //   save: "success"
                  // });
                }
              },
              err => {
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      } else if (this.docType === 'paper') {
        // First file is not required and name is equal to barcode - 2021-07-10
        if (!this.model.numFiles || !this.model.files) {
          this.model.numFiles = 1;
          this.model.files = [{
            docType: "",
            name: this.barCode
          }];
        }
        dataToSend = {
          data: {
            ...this.model,
            property: this.data.propertyId,
            code: this.barCode,
            area: this.data.selectedArea
          },
          docsSelected: this.selectedDocs,
          documentFile: this.form.value.documentFile,
        }

        if (this.selectedStructures.length) {
          dataToSend = {
            ...dataToSend,
            structureSelected: this.selectedStructures,
          };
        }
        if (this.subscriptionType === "FREE" || this.subscriptionType === "FREEBASIC") {
          dataToSend = {
            ...dataToSend,
            structureSelected: this.selectedStructureFree,
          };
        }
        this.subscriptions.push(
          this.documentArchivesService
            .updateMultiPaperDoc(
              this.documentArchive._id,
              dataToSend,
            )
            .pipe(finalize(() => (this.formBtnOptions.active = false)))
            .subscribe(
              res => {
                if (res.status) {
                  this.docUploaded = true;
                  this.toastrService.success(res.message);
                }
              },
              err => {
                this.helperService.handleError(err);
                this.helperService.handleFormlyError(this.form, err);
              }
            )
        );
      }
    }
  }

  /**
   * Handle print file qrCode
   *
   * @since 1.0.0
   */
  onPrint(target: string): void {
    const qrCodes = [];
    if (this.model.files && this.model.files.length) {
      // If we're here we have a paper doc
      this.model.files.forEach(file => {
        if (file.name) {
          qrCodes.push(file.name);
        }
      });
    } else if (this.model.documentFile && this.model.documentFile.length) {
      // If we're here we have a digital doc
      qrCodes.push(this.barCode);
    } else {
      // Paper doc: First file is not required and name is equal to barcode - 2021-07-10
      qrCodes.push(this.barCode);
    }

    if (!qrCodes.length) {
      this.toastrService.error("No qrCodes to inject");
      return;
    }

    let data;

    if (target === "qr") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        docs: this.selectedDocs,
      };
    } else if (target === "module") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        uploadDate: new Date(),
        content: this.model.contenutoDocumento,
        docs: this.selectedDocs,
        structures: this.displayedStructures.length ? this.displayedStructures.filter(el => el.value) : undefined,
      };
    } else {
      this.toastrService.error("missing print target");
    }

    // Open dialog
    this.dialog.open(PrintQrDialogComponent, {
      data,
      width: "600px"
    });
  }
  
  /**
   * Handle filter on docs. A doc hidden by the search is unchecked the same way a click
   * does it, so that selectedDocs, the disabled checkboxes and the form follow what is on
   * screen: a hidden doc is never saved
   *
   * @since 1.0.0
   */
   applyFilter(event: Event): void {
    this.searchValue = (event.target as HTMLInputElement).value;
    const uncheckedDocs = applyDocumentSearch(
      this.topics ? this.topics.documents : undefined,
      this.searchValue,
      this.lang
    );
    uncheckedDocs.forEach((el: any) => {
      this.onSetDoc(el.key, el.schemaType, false, el.translation, el.parentTitle);
    });
  }
}
